import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { communityApi as community } from '../services/api';
import { MemberAvatarWithBadge } from './MemberBadge';
import { Users, MapPin, ArrowRight } from 'lucide-react';

/** Public community teaser — visible to all visitors, no login required.
 *  Three overlapping avatars + live member count + live states + CTA. */
export default function CommunityTeaser() {
  const [stats, setStats] = useState({ totalMembers: 0, statesRepresented: 0, recent: [] });

  useEffect(() => {
    let live = true;
    const load = () => community.publicStats().then(s => { if (live) setStats(s); }).catch(() => {});
    load();
    const t = setInterval(load, 15000); // refresh every 15s for a "live" feel
    return () => { live = false; clearInterval(t); };
  }, []);

  return (
    <section className="max-w-5xl mx-auto px-4 py-10" data-testid="community-teaser">
      <div className="rounded-3xl bg-gradient-to-br from-neutral-900 to-neutral-800 text-white p-6 md:p-10 shadow-2xl relative overflow-hidden">
        <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-green-500/20 blur-3xl"/>
        <div className="relative flex flex-col md:flex-row items-center gap-6">
          {/* Overlapping avatars — colorful, iconized, glow on dark background */}
          <div className="flex -space-x-5" data-testid="community-avatars">
            {(stats.recent.length
              ? stats.recent
              : [
                  { memberType: 'Business Owner' },
                  { memberType: 'Artist' },
                  { memberType: 'Nonprofit' },
                ]
            ).slice(0, 3).map((m, i) => (
              <MemberAvatarWithBadge
                key={i}
                {...m}
                size={68}
                className="ring-4 ring-neutral-900"
                showGlow
              />
            ))}
          </div>

          <div className="flex-1 text-center md:text-left">
            <div className="text-xs uppercase tracking-wider text-green-300 font-semibold">The Community</div>
            <h3 className="mt-1 text-2xl md:text-3xl font-black">
              <span data-testid="community-member-count">{stats.totalMembers.toLocaleString()}</span> members ·{' '}
              <span data-testid="community-states-count">{stats.statesRepresented}</span> states
            </h3>
            <p className="text-neutral-300 text-sm mt-1">Register to meet the full community — see who's building, connecting, and earning.</p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-start">
              <Link to="/signup" data-testid="community-teaser-register"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-500 hover:bg-green-400 text-black font-bold shadow-lg">
                Register to Meet the Community <ArrowRight size={14}/>
              </Link>
              <div className="inline-flex items-center gap-2 text-xs text-neutral-400 px-3 py-2 rounded-full bg-white/5 border border-white/10">
                <Users size={13}/> Live count · updates in real time
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
